import React, { useRef } from 'react';
import { useLocation } from 'react-router';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { LANDING_PAGE_EVENTS } from 'Utilities/analytics';
import { SELL_MY_CAR_LANDING_PAGE_V2 } from 'Utilities/wordpress';

import StickyVRM from '../../../../components/misc/StickyVRM/StickyVRM';
import DialogLoader from '../../../../components/transitions/DialogLoader/DialogLoader';
import { RoundedSection } from '../../common/RoundedSection/RoundedSection';
import { createFAQData } from '../../contentPages/landingPages/FAQSection/FAQSection';
import Status404 from '../../error/404';
import FastlaneHero from '../../fastlane/home/Hero/Hero';
import USP from '../../fastlane/home/Usp/Usp';
import { ExpandableContentWrapper } from '../Components/ExpandableContentWrapper/ExpandableContentWrapper';
import { GuidesSection as Guides } from '../Components/GuidesSection/GuidesSection';
import { Hero as LandingPageHero } from '../Components/Hero/Hero';
import HowItWorks from '../Components/HowItWorks/HowItWorks';
import { HowToSell } from '../Components/HowToSell/HowToSell';
import { LandingPagePartnerLogos } from '../Components/LandingPagePartnerLogos/LandingPagePartnerLogos';
import { RelatedGuidesSectionBespoke } from '../Components/RelatedGuidesSectionBespoke/RelatedGuidesSectionBespoke';
import { SEOSection } from '../Components/SEOSection/SEOSection';
import { Trustpilot } from '../Components/Trustpilot/Trustpilot';
import { useLandingPageData } from '../Hooks/useLandingPageData';
import { useShowLandingPageExperiment } from '../Hooks/useShowLandingPageExperiment';
import LocalTexts from '../landingPageTexts.json';

import { landingPageSectionLoadEvent, PAGE_SECTIONS } from './LandingPage.helpers';

import styles from './LandingPage.scss';

const LocalT = new MDText(LocalTexts);

type HeroSectionProps = {
  description?: string;
  hideBreadcrumb: boolean;
  title?: string;
};

const t = (key: string, args?: Record<string, string | React.ReactNode>) => `${LocalT.translate(key, args)}`;

export const TEXTS = {
  heroSubTitle: t('landingSellMyCarsView.hero.subtitle'),
  heroTitle: t('landingSellMyCarsView.hero.title'),
};

const fastlaneHero = ({ description, hideBreadcrumb, title }: HeroSectionProps) => (
  title && (<FastlaneHero
    analyticsEvents={{
      onLandingPageInputFocus: () => LANDING_PAGE_EVENTS.HERO_VRM_INPUT_FOCUSED(),
    }}
    isHideBreadcrumb={hideBreadcrumb}
    onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.HERO)}
    subTitle={description?.trim() || ''}
    {...{ title }}
  />
  ));

const SellMyCarLandingPageV2 = ({ description, title }: Omit<HeroSectionProps, 'hideBreadcrumb'>) => (
  <div className={cx(styles.component, styles.viewV2)}>
    <div className={styles.heroSection}>
      { fastlaneHero({ description, hideBreadcrumb: false, title }) }
      <USP onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.USP)}/>
    </div>
    <HowItWorks />
    <Trustpilot />
  </div>
);

const LandingPage = () => {
  const $ref = useRef(null);
  const $content = $ref.current;
  const {
    acf,
    getRelatedGuidesContent: getGuidesContent,
    loading,
    stickyVRM,
  } = useLandingPageData();
  const { pathname } = useLocation();
  const { showLandingPagesHeroRedesign } = useShowLandingPageExperiment() || {};

  if (loading) {
    return (<DialogLoader />);
  }

  if (!acf) {
    return (<Status404 />);
  }

  const { header_description: fastlaneHeroDescription, header_title: fastlaneHeroTitle } = acf || {};

  // Testing a leaner version of the '/sell-my-car' landing page
  if (pathname === SELL_MY_CAR_LANDING_PAGE_V2) {
    return (
      <SellMyCarLandingPageV2
        description={fastlaneHeroDescription}
        title={fastlaneHeroTitle}
      />);
  }

  const { faq_title: faqTitle, related_guide_header: relatedGuidesTitle } = acf || {};
  const faq = createFAQData(acf);
  const isFAQEmpty = (!Array.isArray(faq) || !faq.length);

  const relatedGuides = getGuidesContent();
  const isRelatedGuidesEmpty = (!Array.isArray(relatedGuides) || !relatedGuides.length);

  const isHeroExperimentOnLandingPage = showLandingPagesHeroRedesign;

  // Trimmed content version of the landing page
  const isTrimmedContentVersion = (/\/sell-your-car\/.*/).test(pathname);

  return (
    <div ref={$ref} className={styles.component}>
      { stickyVRM && !!$content && !pathname.includes('/mileage') && (
        <StickyVRM
          {...{
            $content,
            mobileOnly: true,
          }}
        />
      )}

      <div className={styles.heroSection}>
        {
          isHeroExperimentOnLandingPage
            ? (<LandingPageHero
              onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.HERO)}
              subTitle={TEXTS.heroSubTitle}
              title={TEXTS.heroTitle}
            />)
            : (fastlaneHero({
              description: fastlaneHeroDescription,
              hideBreadcrumb: isTrimmedContentVersion,
              title: fastlaneHeroTitle,
            }))
        }
        <div className={cx(styles.heroSectionContentWrapper, {
          [styles.trimPadding]: isTrimmedContentVersion,
        })}>
          { !isTrimmedContentVersion && <RoundedSection withBottomLedge theme="off-white">
            <HowToSell {...{ acf }} />
          </RoundedSection> }
          <RoundedSection withBottomLedge withTopLedge>
            <LandingPagePartnerLogos />
          </RoundedSection>
        </div>
      </div>

      <RoundedSection withBottomLedge withTopLedge theme="off-white">
        <SEOSection {...{ acf, isTrimmedContentVersion }} />
      </RoundedSection>

      <RoundedSection withTopLedge theme="off-white">
        <Trustpilot wrapperClassName={styles.trustpilotSection} />
      </RoundedSection>
      {!isFAQEmpty && (
        <ExpandableContentWrapper
          {...{ faq, faqTitle }}
          onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.FAQ)}
        />
      )}
      {!isRelatedGuidesEmpty && (
        <RoundedSection withBottomLedge withTopLedge theme="off-white">
          <RelatedGuidesSectionBespoke
            guides={relatedGuides}
            onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.RELATED_GUIDES)}
            redirectToUrl={pathname}
            sectionTitle={relatedGuidesTitle}
          />
        </RoundedSection>
      )}
      <Guides />
    </div>
  );
};

export default LandingPage;
